/**
 * V13.15 — Page COPF Validations
 *
 * File d'attente dédiée aux demandes de validation ciblant explicitement le
 * COPF (via `target=copf`). Accès : admin OR copf.
 *
 * Route : /copf/validations
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Crown, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, ExternalLink,
  Loader2, ArrowLeft, RefreshCw,
} from "lucide-react";

const STATUSES = [
  { v: "pending", label: "En attente" },
  { v: "validated", label: "Validées" },
  { v: "inconsistency", label: "Incohérences" },
  { v: "all", label: "Toutes" },
];

export default function CopfValidationsPage() {
  const { user, loading: authLoading } = useAuth();
  const isCopf = !!user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // { kind: "approve"|"signal", athlete }
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/federation-validations/copf-queue", { params: { status } });
      setItems(data.items || []);
    } catch (e) { showError(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!authLoading && isCopf) fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, authLoading, isCopf]);

  const submit = async () => {
    if (!action) return;
    setBusy(true);
    try {
      if (action.kind === "approve") {
        await api.post(`/athletes/${action.athlete.id}/validation/approve`, {
          as_role: "copf", // Explicite : validation au titre du COPF
        });
        toast.success("Palmarès validé au titre du COPF.");
      } else if (action.kind === "signal") {
        if (!notes.trim()) { toast.error("Décris l'incohérence."); setBusy(false); return; }
        await api.post(`/athletes/${action.athlete.id}/validation/signal-inconsistency`, { notes: notes.trim() });
        toast.success("Incohérence signalée — l'athlète est notifié.");
      }
      setAction(null); setNotes("");
      await fetchQueue();
    } catch (e) {
      showError(e, { prefix: "Action :" });
    } finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    const c = { pending: 0, validated: 0, inconsistency: 0, all: items.length };
    for (const a of items) { c[a.profile_validation_status || "none"] = (c[a.profile_validation_status || "none"] || 0) + 1; }
    return c;
  }, [items]);

  if (authLoading) {
    return <div className="max-w-6xl mx-auto p-4 md:p-6"><Skeleton className="h-96 w-full" /></div>;
  }
  if (!isCopf) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="copf-val-forbidden">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
          <h1 className="text-xl font-semibold">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mt-1">Réservé au COPF et aux administrateurs.</p>
          <Link to="/"><Button variant="outline" className="mt-4"><ArrowLeft className="h-4 w-4 mr-1.5" />Retour</Button></Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4" data-testid="copf-validations-page">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Crown className="h-6 w-6 text-[hsl(var(--aito-gold))]" />
            Validation des palmarès · COPF
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Fiches d'athlètes ayant demandé la validation officielle du COPF. Vous pouvez valider ou signaler une incohérence.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchQueue} data-testid="copf-val-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Rafraîchir
        </Button>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          {STATUSES.map((s) => (
            <TabsTrigger key={s.v} value={s.v} data-testid={`copf-val-tab-${s.v}`}>
              {s.label}
              {s.v === "pending" && counts.pending > 0 && (
                <Badge className="ml-1.5 bg-amber-500 text-white text-[10px]">{counts.pending}</Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="copf-val-empty">
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
          Aucune fiche en attente de validation COPF sur ce filtre.
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <Card key={a.id} className="p-3 flex items-center gap-3" data-testid={`copf-val-row-${a.id}`}>
              <Avatar className="h-11 w-11 shrink-0">
                <AvatarImage src={a.photo_url || undefined} alt={a.full_name} />
                <AvatarFallback>{(a.full_name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link to={`/athletes/${a.slug || a.id}`} className="font-medium hover:underline">{a.full_name || "?"}</Link>
                  {a.profile_validation_status === "pending" && (
                    <Badge className="bg-amber-500 text-white text-[10px]">En attente</Badge>
                  )}
                  {a.profile_validation_status === "validated" && (
                    <Badge className="bg-emerald-600 text-white text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" />Validé</Badge>
                  )}
                  {a.profile_validation_status === "inconsistency" && (
                    <Badge className="bg-orange-500 text-white text-[10px]"><AlertTriangle className="h-3 w-3 mr-0.5" />Incohérence</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                  {a.main_discipline && <span>{a.main_discipline}</span>}
                  {(a.federations || []).slice(0, 2).map((f, i) => <span key={i}>· {f}</span>)}
                  {(a.commune || a.island) && <span>· {a.commune || a.island}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to={`/athletes/${a.slug || a.id}`} target="_blank">
                  <Button variant="ghost" size="sm" data-testid={`copf-val-open-${a.id}`}>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </Link>
                {a.profile_validation_status !== "validated" && (
                  <Button
                    size="sm"
                    onClick={() => setAction({ kind: "approve", athlete: a })}
                    className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(43_74%_45%)]"
                    data-testid={`copf-val-approve-${a.id}`}
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />Valider
                  </Button>
                )}
                {a.profile_validation_status !== "inconsistency" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-orange-500 text-orange-600"
                    onClick={() => setAction({ kind: "signal", athlete: a })}
                    data-testid={`copf-val-signal-${a.id}`}
                  >
                    <AlertTriangle className="h-3.5 w-3.5 mr-1" />Signaler
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setNotes(""); } }}>
        <DialogContent data-testid="copf-val-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {action?.kind === "approve"
                ? `Valider au titre du COPF · ${action.athlete?.full_name}`
                : `Signaler une incohérence · ${action?.athlete?.full_name}`}
            </DialogTitle>
            <DialogDescription>
              {action?.kind === "approve"
                ? "Le palmarès sera officiellement validé au nom du COPF. Un sceau premium apparaîtra sur la fiche et le PDF de l'athlète."
                : "Décris précisément l'incohérence constatée. L'athlète recevra une notification et pourra corriger sa fiche."}
            </DialogDescription>
          </DialogHeader>
          {action?.kind === "signal" && (
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex. : Le résultat XYZ est incorrect. Le vrai classement est…" data-testid="copf-val-notes" />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAction(null); setNotes(""); }} disabled={busy} data-testid="copf-val-cancel">Annuler</Button>
            <Button
              onClick={submit}
              disabled={busy}
              className={action?.kind === "approve"
                ? "bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(43_74%_45%)]"
                : "bg-orange-500 text-white hover:bg-orange-600"}
              data-testid="copf-val-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : (action?.kind === "approve" ? <ShieldCheck className="h-4 w-4 mr-1.5" /> : <AlertTriangle className="h-4 w-4 mr-1.5" />)}
              {action?.kind === "approve" ? "Valider" : "Signaler"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
